import AdminHeader from '@Components/common/AdminHeader';
import { FlexRow } from '@Components/common/Layouts';
import ToolTip from '@Components/RadixComponents/ToolTip';
import { useSpring, animated } from '@react-spring/web';
import React, { useEffect, useState } from 'react';
import DataTable from '@Components/common/DataTable';
import { deleteUserManual, getUserManual } from '@Services/tutorials';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import DeleteConfirmationOverlay from '@Components/common/PopupOverlays/DeleteConfirmationOverlay';
import Portal from '@Components/common/Layouts/Portal';
import { useLocation, useNavigate } from 'react-router-dom';
import hasErrorBoundary from '@Components/common/hasErrorBoundary';
import TutorialsHeaderSwitchTab from '../TutorialsHeader';
import UserManualRegistrationForm from './UserManualRegistrationForm';

const Manual = () => {
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string>('');
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const queryClient = useQueryClient();

  const {
    mutate: deleteUserManualData,
    isError,
    isLoading,
    error,
  } = useMutation({
    mutationFn: () => deleteUserManual(idToDelete),
    onSuccess: () => {
      toast.success('Manual deleted successfully');
      queryClient.invalidateQueries(['user-manual-table-data']);
      setOpenDeleteConfirmation(false);
    },
    onError: () => {
      toast.error('Failed to delete manual');
    },
  });

  const userManualDataColumn = [
    {
      header: 'S.N',
      accessorKey: '',
      cell: ({ row }: Record<string, any>) => {
        // eslint-disable-next-line
        return row?.index + 1;
      },
    },
    {
      header: 'TITLE',
      accessorKey: 'title',
    },
    {
      header: 'DESCRIPTION',
      accessorKey: 'description',
    },
    {
      header: '',
      accessorKey: 'icon',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }: any) => {
        const cellId = row?.original?.id;
        return (
          <FlexRow className="naxatw-relative naxatw-mr-6 naxatw-mt-2 naxatw-cursor-pointer naxatw-justify-end naxatw-gap-4">
            <ToolTip
              name="edit"
              message="Edit"
              className="naxatw-text-icon-md naxatw-text-matt-100 hover:naxatw-text-calm-teal"
              iconClick={() => {
                setShowRegistrationForm(true);
                navigate(`/data-bank/tutorials/manual/edit/${cellId}`);
              }}
            />
            <ToolTip
              name="delete"
              message="Delete"
              className="naxatw-text-icon-md naxatw-text-matt-100 naxatw-duration-200 hover:naxatw-text-red-500"
              iconClick={() => {
                setOpenDeleteConfirmation(true);
                setIdToDelete(cellId);
              }}
            />
          </FlexRow>
        );
      },
    },
  ];
  const springs = useSpring({
    from: { y: 100 },
    to: { y: 0 },
  });

  useEffect(() => {
    if (pathname.includes('edit') || pathname.includes('add')) {
      setShowRegistrationForm(true);
    } else {
      setShowRegistrationForm(false);
    }
  }, [pathname]);
  return (
    <>
      <div className="naxatw-space-y-6">
        <AdminHeader
          title="Tutorials"
          onAddNewClick={() => {
            setShowRegistrationForm(true);
            navigate('/data-bank/tutorials/manual/add');
          }}
          hasBulkUpload={false}
          needSearchBar={false}
          registerButtonText="Add Manual"
        />
        <TutorialsHeaderSwitchTab />
        <animated.div
          style={{ ...springs }}
          className="naxatw-h-[calc(100vh-18rem)] naxatw-w-full naxatw-overflow-hidden lg:naxatw-h-[calc(100vh-15.6rem)]"
        >
          <DataTable
            columns={userManualDataColumn}
            queryFn={getUserManual}
            queryKey="user-manual-table-data"
            searchInput=""
            sortByKey
          />
        </animated.div>
      </div>
      {showRegistrationForm && (
        <Portal>
          <UserManualRegistrationForm
            onClose={() => setShowRegistrationForm(false)}
          />
        </Portal>
      )}
      {openDeleteConfirmation && (
        <Portal>
          <DeleteConfirmationOverlay
            onClose={() => setOpenDeleteConfirmation(false)}
            onDelete={() => deleteUserManualData()}
            isError={isError}
            isLoading={isLoading}
            error={error}
          />
        </Portal>
      )}
    </>
  );
};

export default hasErrorBoundary(Manual);
